import React, { useState, useMemo } from 'react';
import './ChatList.css';

/**
 * 🏷️ Helper to resolve chat name, avatar, and partner data
 */
const resolveChatMeta = (chat, currentUserId) => {
    const isGroup = chat?.type === 'GROUP' || chat?.isGroup === true;
    let displayName = chat?.chatName || chat?.nickname || chat?.username || (isGroup ? 'Group Chat' : 'Direct Message');
    let avatarUrl = chat?.chatPicture || chat?.profilePicture || (isGroup ? '/group-avatar.png' : '/default-avatar.png');

    let partnerData = null;
    if (!isGroup) {
        const members = Array.isArray(chat?.members) ? chat.members :
            Array.isArray(chat?.participants) ? chat.participants : [];

        // Find the other person in the chat
        partnerData = members.find((m) => {
            const mId = m?._id || m?.id || m;
            return String(mId) !== String(currentUserId);
        }) || null;

        if (partnerData && typeof partnerData === 'object') {
            displayName = partnerData.nickname || partnerData.username || displayName;
            avatarUrl = partnerData.profilePicture || avatarUrl;
        }
    }

    const nameToShow = isGroup ? displayName : displayName?.startsWith('@') ? displayName : `@${displayName}`;
    return { isGroup, displayName, nameToShow, avatarUrl, partnerData };
};

const ChatList = ({
    items = [],
    selectedId,
    onSelect = () => { },
    notifications = {},
    currentUserId,
    messagesByChatId = {},
    pinnedIds = [],
    onAction = () => { },
    currentTab = 'all', // ✅ NEW: Defaults to 'all'
}) => {
    const [activeMenu, setActiveMenu] = useState(null);
    const resolvedUserId = String(currentUserId || '');

    // 🔍 FILTERING LOGIC
    // This organizes the list based on the active tab selected in the sidebar
    const filteredItems = useMemo(() => {
        if (!Array.isArray(items)) return [];

        switch (currentTab) {
            case 'unread':
                // ✅ Show only chats that have a notification count > 0
                return items.filter(chat => {
                    const id = String(chat?._id || chat?.id);
                    return (notifications[id] || 0) > 0;
                });

            case 'groups':
                // ✅ Show only Group Chats
                return items.filter(chat => chat?.isGroup === true || chat?.type === 'GROUP');

            case 'favorites':
                // ✅ Show only pinned/favorited chats
                return items.filter(chat => {
                    const id = String(chat?._id || chat?.id);
                    return Array.isArray(pinnedIds) && pinnedIds.some(pId => String(pId) === id);
                });

            case 'all':
            default:
                return items;
        }
    }, [items, notifications, currentTab, pinnedIds]);

    if (!resolvedUserId) return <p className="chat-list-empty">Authenticating...</p>;

    // Handle empty states for different tabs
    if (filteredItems.length === 0) {
        const emptyMessage = currentTab === 'unread'
            ? "No Unread Messages! 🎉"
            : currentTab === 'groups'
                ? "No Group Chats Found"
                : currentTab === 'favorites'
                    ? "No Favorites Found"
                    : "No Conversations Yet";

        return (
            <div className="chat-list-empty-container">
                <p className="chat-list-empty">{emptyMessage}</p>
                {currentTab === 'all' && (
                    <p className="chat-list-subtext">Start a chat from your friends list!</p>
                )}
            </div>
        );
    }

    const formatTimestamp = (timestamp) => {
        if (!timestamp) return '';
        const date = new Date(timestamp);
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    return (
        <ul className="chat-list">
            {/* ✅ Map over filteredItems instead of raw items */}
            {filteredItems.map((chat) => {
                const id = chat?._id || chat?.id;
                if (!id) return null;
                const idStr = String(id);
                const isSelected = selectedId && String(selectedId) === idStr;
                const unreadCount = notifications?.[idStr] || 0;
                const hasUnread = unreadCount > 0;

                const { isGroup, nameToShow, avatarUrl, partnerData } = resolveChatMeta(chat, resolvedUserId);

                // Online status logic
                const statusClass = partnerData?.isOnline ? 'online' : 'offline';

                // Message Preview Logic
                const metaLastMessage = chat?.lastMessage || chat?.latestMessage || null;
                const loadedMsgs = messagesByChatId?.[idStr];
                const loadedLastMessage = Array.isArray(loadedMsgs) && loadedMsgs.length ? loadedMsgs[loadedMsgs.length - 1] : null;
                const lastMessage = loadedLastMessage || metaLastMessage;

                const content = lastMessage?.content || 'No messages yet';
                const preview = typeof content === 'string' && content.length > 30 ? `${content.slice(0, 30)}…` : content;
                const previewTime = formatTimestamp(lastMessage?.createdAt);
                const isPinned = Array.isArray(pinnedIds) && pinnedIds.some(pId => String(pId) === idStr);

                return (
                    <li
                        key={idStr}
                        onClick={() => onSelect(chat)}
                        className={`chat-list-item ${isGroup ? 'group-item' : 'dm-item'} ${isSelected ? 'active' : ''} ${hasUnread ? 'has-unread' : ''}`}
                    >
                        {/* Avatar Section */}
                        <div className="photo" style={{ backgroundImage: `url(${avatarUrl})` }}>
                            {!isGroup && <div className={`status-dot ${statusClass}`} />}
                            {hasUnread && <div className="unread-badge">{unreadCount > 99 ? '99+' : unreadCount}</div>}
                        </div>

                        {/* Middle Text Area */}
                        <div className="desc-contact">
                            <p className="name">{nameToShow}</p>
                            <p className="message">{preview}</p>
                        </div>

                        {/* Right Section: Time & Pinned/Menu */}
                        <div className="chat-list-right">
                            <div className="timer">{previewTime}</div>

                            <div className="chat-item-icons">
                                {isPinned && <span className="pin-indicator">📌</span>}

                                <div className="dot-menu-container">
                                    <button
                                        className="dot-menu-trigger"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setActiveMenu(activeMenu === idStr ? null : idStr);
                                        }}
                                    >
                                        ⋮
                                    </button>

                                    {activeMenu === idStr && (
                                        <ul className="options-dropdown">
                                            <li onClick={(e) => { e.stopPropagation(); onAction(idStr, 'archive'); setActiveMenu(null); }}>Archive</li>
                                            <li onClick={(e) => { e.stopPropagation(); onAction(idStr, 'pin'); setActiveMenu(null); }}>{isPinned ? 'Unpin' : 'Pin'}</li>
                                            <li onClick={(e) => { e.stopPropagation(); onAction(idStr, 'favourite'); setActiveMenu(null); }}>Favourites</li>
                                            <li onClick={(e) => { e.stopPropagation(); onAction(idStr, 'mute'); setActiveMenu(null); }}>Mute</li>
                                            <hr />
                                            <li onClick={(e) => { e.stopPropagation(); onAction(idStr, 'clear'); setActiveMenu(null); }}>Clear Chat</li>
                                            <li onClick={(e) => { e.stopPropagation(); onAction(idStr, 'delete'); setActiveMenu(null); }} className="danger">Delete</li>
                                            <li onClick={(e) => { e.stopPropagation(); onAction(idStr, 'block'); setActiveMenu(null); }} className="danger">Block</li>
                                        </ul>
                                    )}
                                </div>
                            </div>
                        </div>
                    </li>
                );
            })}
        </ul>
    );
};

export default ChatList;